import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MatLegacyFormFieldAppearance as MatFormFieldAppearance } from '@angular/material/legacy-form-field';
import { makeProvider } from '@shared/form-fields/make-provider';
import { get, isObject, isString } from 'lodash-es';
import { AbstractFormFieldComponent } from '../abstract-form-field/abstract-form-field.component';

@Component({
    selector: 'app-input-autocomplete',
    styleUrls: ['./autocomplete-input.component.scss'],
    template: `
        <mat-form-field [appearance]="appearance">
            <mat-label *ngIf="label">
                {{ label }}
            </mat-label>

            <div matPrefix>
                <ng-content select="[appPrefix]"></ng-content>
            </div>

            <input
                type="text"
                matInput
                [(ngModel)]="searchText"
                [ngModelOptions]="{ standalone: true }"
                (ngModelChange)="onInputChange($event)"
                [placeholder]="placeholder"
                [disabled]="disabled"
                [readonly]="readonly"
                [required]="required"
                [matAutocomplete]="auto"
                [maxlength]="maxlength"
                [minlength]="minlength"
            />

            <div matSuffix>
                <ng-content select="[appSuffix]"></ng-content>
            </div>

            <mat-hint *ngIf="hint">
                {{ hint }}
            </mat-hint>

            <mat-error *ngFor="let message of errorMessages">{{ message }}</mat-error>
        </mat-form-field>

        <mat-autocomplete #auto="matAutocomplete"
                          autoActiveFirstOption
                          [displayWith]="displayWith"
                          (optionSelected)="onOptionSelected($event)">
            <mat-option *ngFor="let option of filteredSuggestions" [value]="option">
                {{ optionNameProp ? option[optionNameProp] : option }}
            </mat-option>
        </mat-autocomplete>
    `,
    providers: [makeProvider(AutocompleteInputComponent)],
})
export class AutocompleteInputComponent extends AbstractFormFieldComponent<string> {
    filteredSuggestions: any = [];

    _selectedItem: any;
    _searchText: string;
    _selectedId: string;

    @Input() appearance: MatFormFieldAppearance = 'outline';
    @Input() suggestions: any = [];
    @Input() optionNameProp: string;
    @Input() maxlength?: number;
    @Input() minlength?: number;

    @Input()
    get selectedItem() {
        return this._selectedItem;
    }

    set selectedItem(value) {
        this._selectedItem = value;

        this.selectedItemChange.emit(this._selectedItem);
    }

    @Input()
    get selectedId() {
        return this._selectedId;
    }

    set selectedId(value) {
        this._selectedId = value;

        this.selectedIdChange.emit(this._selectedId);
    }

    @Input()
    get searchText() {
        return this._searchText;
    }

    set searchText(value) {
        this._searchText = value;

        this.value = value;

        this.searchTextChange.emit(this._searchText);
    }

    @Output() selectedItemChange = new EventEmitter<any>();
    @Output() selectedIdChange = new EventEmitter<string>();
    @Output() searchTextChange = new EventEmitter<string>();

    writeValue(value: string) {
        this.searchText = value;
        this._value = value;

        this.onChange(value);
    }

    private _filter(value: string) {
        if (typeof value !== 'string') {
            return;
        }

        const filterValue = value.toLowerCase();

        if (!filterValue) {
            return this.suggestions;
        }

        if (this.suggestions.length) {
            return this.suggestions.filter(option => {
                if (isObject(option) && !Array.isArray(option)) {
                    if (this.optionNameProp) {
                        return option[this.optionNameProp].toLowerCase().includes(filterValue);
                    } else {
                        throw Error('optionNameProp must be provided for object options');
                    }
                }

                if (isString(option)) {
                    return option.toLowerCase().includes(filterValue);
                }
            });
        }
    }

    displayWith(option: any) {
        if (option) {
            if (this.optionNameProp) {
                return option[this.optionNameProp];
            }
            return option.label ? option.label : option.name ? option.name : option;
        }
    }

    onOptionSelected($event) {
        const optionValue: any = $event.option.value;

        if (optionValue && isObject(optionValue) && !Array.isArray(optionValue)) {
            this.selectedItem = optionValue;
            this.selectedId = get(optionValue, 'id');
            this.searchText = get(optionValue, 'name');
        }

        if (isString(optionValue)) {
            this.searchText = optionValue;
        }
    }

    onInputChange($event) {
        this.filteredSuggestions = this._filter($event);

        if (typeof $event === 'string') {
            const newSearchText = $event.toLowerCase().trim();

            const existingOption = this.suggestions.find(option => {
                if (isObject(option) && !Array.isArray(option)) {
                    if (this.optionNameProp) {
                        return option[this.optionNameProp].toLowerCase() === newSearchText;
                    } else {
                        throw new Error('optionNameProp must be provided for object options');
                    }
                }

                if (isString(option)) {
                    return option.toLowerCase() === newSearchText;
                }
            });

            if (existingOption) {
                this.selectedItem = existingOption;
                this.selectedId = existingOption.id;
            } else if (this.selectedItem) {
                if (isObject(this.selectedItem) && this.selectedItem[this.optionNameProp] !== newSearchText) {
                    this.selectedItem = null;
                    this.selectedId = null;
                }

                if (isString(this.selectedItem) && this.selectedItem !== newSearchText) {
                    this.selectedItem = null;
                    this.selectedId = null;
                }
            }
        }
    }
}
